import { useState, useEffect } from "react";
import { useParams, useLocation } from "wouter";
import { useQuery, useMutation } from "@tanstack/react-query";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import { useToast } from "@/hooks/use-toast";
import { apiRequest } from "@/lib/queryClient";
import {
  MessageSquare,
  Check,
  AlertCircle,
  Loader2,
  Eye,
  EyeOff,
  Shield,
  ArrowLeft,
} from "lucide-react";

interface InviteValidation {
  valid: boolean;
  expired?: boolean;
  used?: boolean;
  targetEmail?: string;
  message?: string;
}

export default function ConnectSmsPage() {
  const { token } = useParams<{ token: string }>();
  const [, navigate] = useLocation();
  const { toast } = useToast();
  
  const [showAuthToken, setShowAuthToken] = useState(false);
  const [accountSid, setAccountSid] = useState("");
  const [authToken, setAuthToken] = useState("");
  const [fromNumber, setFromNumber] = useState("");
  const [providerName, setProviderName] = useState("My Twilio Account");
  const [submitted, setSubmitted] = useState(false);

  // Validate the invite token
  const { data: validation, isLoading: validating, error } = useQuery<InviteValidation>({
    queryKey: ["/api/sms-connect", token],
    queryFn: async () => {
      const res = await fetch(`/api/sms-connect/${token}`);
      const data = await res.json();
      if (!res.ok) {
        return { 
          valid: false, 
          expired: data.message?.includes('expired'),
          used: data.message?.includes('used'),
          message: data.message 
        };
      }
      return data;
    },
    enabled: !!token,
  });

  // Submit credentials mutation
  const submitMutation = useMutation({
    mutationFn: async (data: { token: string; providerName: string; accountSid: string; authToken: string; fromNumber: string }) => {
      const res = await apiRequest("POST", `/api/sms-connect/${data.token}`, {
        name: data.providerName,
        accountSid: data.accountSid,
        authToken: data.authToken,
        fromNumber: data.fromNumber,
      });
      return res.json();
    },
    onSuccess: () => {
      setSubmitted(true);
      toast({
        title: "SMS Provider Connected",
        description: "Your Twilio credentials have been saved successfully.",
      });
    },
    onError: (error: any) => {
      toast({
        title: "Connection Failed",
        description: error.message || "Failed to save your SMS credentials. Please try again.",
        variant: "destructive",
      });
    },
  });

  if (validating) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-background p-4">
        <Card className="w-full max-w-md">
          <CardContent className="py-12 flex flex-col items-center gap-4">
            <Loader2 className="w-8 h-8 animate-spin text-primary" />
            <p className="text-muted-foreground">Validating invite...</p>
          </CardContent>
        </Card>
      </div>
    );
  }

  if (!validation?.valid) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-background p-4">
        <Card className="w-full max-w-md">
          <CardHeader className="text-center">
            <div className="w-16 h-16 mx-auto rounded-full bg-destructive/10 flex items-center justify-center mb-4">
              <AlertCircle className="w-8 h-8 text-destructive" />
            </div>
            <CardTitle>Invalid Invite</CardTitle>
            <CardDescription>
              {validation?.expired 
                ? "This invite link has expired. Please request a new one from your administrator."
                : validation?.used
                  ? "This invite link has already been used."
                  : validation?.message || "This invite link is not valid."}
            </CardDescription>
          </CardHeader>
          <CardContent className="flex justify-center">
            <Button variant="outline" onClick={() => navigate("/")}>
              <ArrowLeft className="w-4 h-4 mr-2" />
              Go to Homepage
            </Button>
          </CardContent>
        </Card>
      </div>
    );
  }

  if (submitted) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-background p-4">
        <Card className="w-full max-w-md">
          <CardHeader className="text-center">
            <div className="w-16 h-16 mx-auto rounded-full bg-green-500/10 flex items-center justify-center mb-4">
              <Check className="w-8 h-8 text-green-600" />
            </div>
            <CardTitle>Connection Successful</CardTitle>
            <CardDescription>
              Your SMS provider has been connected. You can now close this page.
            </CardDescription>
          </CardHeader>
          <CardContent className="flex justify-center">
            <Button variant="outline" onClick={() => navigate("/")}>
              <ArrowLeft className="w-4 h-4 mr-2" />
              Go to Dashboard
            </Button>
          </CardContent>
        </Card>
      </div>
    );
  }

  return (
    <div className="min-h-screen flex items-center justify-center bg-background p-4">
      <Card className="w-full max-w-lg">
        <CardHeader className="text-center">
          <div className="w-16 h-16 mx-auto rounded-full bg-primary/10 flex items-center justify-center mb-4">
            <MessageSquare className="w-8 h-8 text-primary" />
          </div>
          <CardTitle>Connect Your SMS Provider</CardTitle>
          <CardDescription>
            Enter your Twilio credentials to enable SMS functionality
          </CardDescription>
          <Badge variant="outline" className="w-fit mx-auto mt-2">
            <Shield className="w-3 h-3 mr-1" />
            Secure Connection
          </Badge>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="space-y-2">
            <Label htmlFor="providerName">Display Name</Label>
            <Input
              id="providerName"
              placeholder="e.g., My Twilio Account"
              value={providerName}
              onChange={(e) => setProviderName(e.target.value)}
              data-testid="input-provider-name"
            />
            <p className="text-xs text-muted-foreground">
              A friendly name to identify this connection
            </p>
          </div>
          
          <div className="space-y-2">
            <Label htmlFor="accountSid">Account SID</Label>
            <Input
              id="accountSid"
              placeholder="ACxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxx"
              value={accountSid}
              onChange={(e) => setAccountSid(e.target.value)}
              data-testid="input-account-sid"
            />
            <p className="text-xs text-muted-foreground">
              Find this in your Twilio Console
            </p>
          </div>
          
          <div className="space-y-2">
            <Label htmlFor="authToken">Auth Token</Label>
            <div className="relative">
              <Input
                id="authToken"
                type={showAuthToken ? "text" : "password"}
                placeholder="Your Twilio Auth Token"
                value={authToken}
                onChange={(e) => setAuthToken(e.target.value)}
                data-testid="input-auth-token"
              />
              <Button
                type="button"
                variant="ghost"
                size="icon"
                className="absolute right-0 top-0 h-full"
                onClick={() => setShowAuthToken(!showAuthToken)}
              >
                {showAuthToken ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
              </Button>
            </div>
            <p className="text-xs text-muted-foreground">
              Keep this secret - it grants access to your Twilio account
            </p>
          </div>
          
          <div className="space-y-2">
            <Label htmlFor="fromNumber">From Phone Number</Label>
            <Input
              id="fromNumber"
              placeholder="+1XXXXXXXXXX"
              value={fromNumber}
              onChange={(e) => setFromNumber(e.target.value)}
              data-testid="input-from-number"
            />
            <p className="text-xs text-muted-foreground">
              Must be a verified Twilio phone number in E.164 format
            </p>
          </div>

          <Button
            className="w-full"
            onClick={() => submitMutation.mutate({ 
              token: token!, 
              providerName, 
              accountSid, 
              authToken, 
              fromNumber 
            })}
            disabled={!accountSid || !authToken || !fromNumber || submitMutation.isPending}
            data-testid="button-connect-sms"
          >
            {submitMutation.isPending ? (
              <>
                <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                Connecting...
              </>
            ) : (
              <>
                <Check className="w-4 h-4 mr-2" />
                Connect SMS Provider
              </>
            )}
          </Button>

          <p className="text-xs text-center text-muted-foreground pt-2">
            Your credentials are encrypted and stored securely.
          </p>
        </CardContent>
      </Card>
    </div>
  );
}
